import React, { ComponentType } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { compose } from "redux";
import {
  FEATURE_FLAGS,
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import {
  UserAttributes,
  getUserAttribute,
  checkIsCastingBBC,
  getUserGroup,
  doesUserHaveEntitlements,
  withAuth,
  IWithAuthProps,
  UserGroupAccountTypeMap,
  oidcLogout,
} from "shared-auth";
import {
  getNavHeaderLinks,
  getPerformersSearchText,
  INavigationHeaderHandlers,
  myAccountLink,
  NavigationHeader,
} from "component-library";
import { AddOnEntitlements, PremiumEntitlements } from "shared-types";
import { withTranslation, WithTranslation } from "i18n";
import { UserRoleManager, UserRoles } from "core";

import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";

import { GlobalEventTypes } from "contexts/GlobalContext";
import PerformersService from "services/PerformersService";

import { getSearchAndFiltersFrontendUrl } from "services/UrlBuilder";

import { LOGO_LINK } from "./HeaderSectionContent";
import "./HeaderSection.scss";
import {
  IPerformerSearchProps,
  IPerformerSearchResult,
} from "component-library/src/components/Organisms/NavigationHeader/PerformerSearch/PerformerSearch";

interface IHeaderSectionReceivedProps {
  menuContainerRef: React.RefObject<Element>;
}

export interface IHeaderSectionProps
  extends IGlobalContextProps,
    RouteComponentProps,
    IHeaderSectionReceivedProps,
    LaunchDarklyClientWithFlags,
    WithTranslation,
    IWithAuthProps {}

export class HeaderSection extends React.Component<IHeaderSectionProps> {
  private readonly navigationHeaderHandlers: INavigationHeaderHandlers;
  private popupRef = React.createRef<HTMLDivElement>();

  constructor(props: IHeaderSectionProps) {
    super(props);

    this.navigationHeaderHandlers = {
      onSignInHandler: this.signIn,
      onSignOutHandler: this.signOut,
      onClickAccountHandler: this.accountClickHandler,
      onLocalizationChangedHandler: this.localizationChanged,
    };
  }

  public render() {
    const { t, auth } = this.props;
    const isSignedIn = auth.isAuthenticated;

    const userName = getUserAttribute(UserAttributes.FullName);
    const performerSearchProps = this.getPerformerSearchProps();

    const memberGroup = getUserGroup();
    const isCastingBBC = checkIsCastingBBC();
    const isPremiumLEGACY = doesUserHaveEntitlements(
      PremiumEntitlements.Premium_Site_Access
    );
    const isAddonPerformer =
      doesUserHaveEntitlements(AddOnEntitlements.AddOnBundle) ||
      isPremiumLEGACY;
    const isAgencySettingsFlagEnabled =
      this.props.flags[FEATURE_FLAGS.AgencySettings];
    const isSelfTapesEnabled = this.props.flags[FEATURE_FLAGS.SelfTapes];
    const isFilmmakersPhase4Enabled =
      this.props.flags[FEATURE_FLAGS.FilmmakersPhase4];

    const { lhNavBottomLinks, headerLabels, localPopUpText, mainNav } =
      getNavHeaderLinks(memberGroup, {
        isCastingBBC,
        isAddonPerformer,
        isAgencySettingsFlagEnabled,
        isSelfTapesEnabled,
        isFilmmakersPhase4Enabled,
      });

    if (auth.isLoading && auth.activeNavigator !== "signinSilent") return null;

    return (
      <React.Fragment>
        <NavigationHeader
          menuContainer={this.props.menuContainerRef}
          popupContainer={this.popupRef}
          menuClassName="c-header-section__navigation-menu"
          accountType={UserGroupAccountTypeMap[memberGroup]}
          userName={userName}
          userLinkUrl={myAccountLink}
          headerLabels={headerLabels}
          logoRedirectionLink={LOGO_LINK}
          links={[]}
          bottomNavLinks={lhNavBottomLinks}
          mainNavData={{
            handleNavigationItemClick: this.redirectToUrl,
            handleSubNavigationItemClick: this.redirectToUrl,
            ...mainNav,
          }}
          signedIn={isSignedIn}
          handlers={this.navigationHeaderHandlers}
          localizationTexts={localPopUpText}
          performerSearchProps={performerSearchProps}
          t={t}
        />
        <div ref={this.popupRef} />
      </React.Fragment>
    );
  }

  private localizationChanged = () =>
    this.props.globalContext.notifyListener(
      GlobalEventTypes.reloadLocalization
    );

  private accountClickHandler = () => window.location.assign(myAccountLink);

  private signIn = () => this.props.auth.signinRedirect();

  private signOut = () => oidcLogout();

  private redirectToUrl =
    (event: React.MouseEvent<HTMLAnchorElement>, text: string, link: string) =>
    () =>
      window.location.assign(link);

  private loadSearchResults = async (
    searchTerm: string
  ): Promise<IPerformerSearchResult[]> =>
    await PerformersService.searchPerformers(searchTerm);

  private redirectToSearchSiteUrl = (href: string) => {
    const link = getSearchAndFiltersFrontendUrl(href);

    if (window.open(link, "_blank") === null) {
      window.location.replace(link);
    }
  };

  private showMoreResults = (searchTerm: string) =>
    this.redirectToSearchSiteUrl(`?performerName=${searchTerm}`);

  private getPerformerSearchProps = (): IPerformerSearchProps | undefined => {
    if (UserRoleManager.isInRole(UserRoles.castingDirector)) {
      return {
        content: getPerformersSearchText(),
        loadSearchResults: this.loadSearchResults,
        showMoreResults: this.showMoreResults,
      };
    }

    return undefined;
  };
}

export default compose(
  withAuth,
  withLaunchDarklyConsumerHOC(),
  withGlobalContext,
  withRouter,
  withTranslation()
)(HeaderSection) as ComponentType<
  React.PropsWithChildren<IHeaderSectionReceivedProps>
>;
